<script setup>
import Default from '@/Layouts/Default.vue';
import CourseHeader from '@/Components/Content/Header/Course.vue';
import Content from '@/Components/Content/index.vue';
import AccordianGroup from '@/Components/Accordion/Group.vue';
import AccordianItem from '@/Components/Accordion/Item.vue';
import RelatedCourses from '@/Components/Related/Courses.vue';
import RelatedCareers from '@/Components/Related/Careers.vue';
import Heading from '@/Components/Heading.vue';
import { Head, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import SiteItem from "@/Components/Accordion/ItemContents/SiteItem.vue";
import { AUSTRALIA_COUNTRY_NAME } from '@/constants';

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
  relatedCourses: {
    type: Object,
    required: true,
  },
  relatedCareers: {
    type: Object,
    required: true,
  },
});

const page = usePage();
const context = computed(() => page.props?.context);

const sites = computed(() => {
  // Use deep copy of prop data. Shallow copy is not good enough and causes
  // DOMException: Failed to execute 'replaceState' on 'History': #<Object> could not be cloned.
  const sites = JSON.parse(JSON.stringify(props.course.sites));

  const filteredSites = sites.filter((site, index) => {
    site.attendance = site.attendance.filter((attendance) => attendance.name !== 'Part-time');
    return (
      site.country.name !== AUSTRALIA_COUNTRY_NAME ||
      site.cricos_code
    );
  });

  if (context.value?.search_filters?.all_scoped_states) {
    filteredSites.sort((a, b) => {
      const aIsPriority = context.value.search_filters.all_scoped_states.includes(a.state);
      const bIsPriority = context.value.search_filters.all_scoped_states.includes(b.state);
      if (aIsPriority === bIsPriority) return 0;
      return aIsPriority ? -1 : 1;
    });
  }

  return filteredSites;
})
const hasELTRequirements = computed(() => {
  return sites.value.some((site) => {
    return site.overall_toefl_score || site.overall_ielts_score;
  });
});

const providerName = context.value.name;
</script>


<template>
  <Head>
    <title>{{props.course.name}} - {{ providerName }}</title>
    <meta name="description" :content="props.course.description">
  </Head>
  <Default>
    <Content>
      <template #header>
        <CourseHeader :course="course"/>
      </template>
      <template #content>
        <Heading
          size="h2"
          style-size="h2"
          class="mb-3"
        >
          Course Details
        </Heading>

        <p
          v-if="course.description"
          class="my-6"
        >
          {{ course.description }}
        </p>
        <p
          v-if="course.qualification.name"
          class="my-6"
        >
          <strong>Qualification: </strong> {{ course.qualification.name }}
        </p>
        <p
          v-if="course.structure"
          class="my-6"
        >
          <strong>Course structure: </strong>{{ course.structure }}
        </p>
        <p
          v-if="course.other_info"
          class="my-6"
        >
          {{ course.other_info }}
        </p>

        <AccordianGroup>
          <AccordianItem :is-open="true">
            <template #title>
              Key Information
            </template>
            <template #content>
                <div>
                    <SiteItem v-for="(site, index) in sites" :key="`course-site-${index}`" :site="site" :course="course" />
                    <p v-if="hasELTRequirements" class="text-xs mt-3">* Note that TOEFL and IELTS scores are indicative of overall requirements in either test. Applicants are strongly encouraged to contact the educator for a more detailed breakdown of requirements. Note also that where TOEFL and IELTS scores are published applicants will typically require one or the other.
                    </p>
                </div>

            </template>
          </AccordianItem>
          <AccordianItem v-if="course.entry_requirements">
            <template #title>
              Entry Criteria
            </template>
            <template #content>
              {{ course.entry_requirements }}
            </template>
          </AccordianItem>
          <AccordianItem v-if="relatedCourses.data && relatedCourses.data.length">
            <template #title>
              Related Courses
            </template>
            <template #content>
              <RelatedCourses :courses="relatedCourses.data" />
            </template>
          </AccordianItem>
          <AccordianItem v-if="course.careers.length || relatedCareers.data.length">
            <template #title>
              Related Careers
            </template>
            <template #content>
              <RelatedCareers :careers="relatedCareers" :acirdata="course.careers"/>
            </template>
          </AccordianItem>
        </AccordianGroup>
      </template>
    </Content>
  </Default>
</template>
